import type { CallStatus, CallDirection, CallOutcome, CallTransport } from '../schemas/call.schema';

// ─── Dynamic outcomes ────────────────────────────────────────────────────
// Outcome definitions live in the outcome_definitions table; each row stores a
// `color` name that maps to badge classes here. Class strings must stay
// literal so Tailwind's scanner keeps them.
export const OUTCOME_COLOR_CLASSES: Record<string, string> = {
  slate: 'bg-slate-100 text-slate-900',
  gray: 'bg-gray-100 text-gray-900',
  red: 'bg-red-100 text-red-900',
  orange: 'bg-orange-100 text-orange-900',
  amber: 'bg-amber-100 text-amber-900',
  yellow: 'bg-yellow-100 text-yellow-900',
  lime: 'bg-lime-100 text-lime-900',
  green: 'bg-green-100 text-green-900',
  emerald: 'bg-emerald-100 text-emerald-900',
  teal: 'bg-teal-100 text-teal-900',
  cyan: 'bg-cyan-100 text-cyan-900',
  sky: 'bg-sky-100 text-sky-900',
  blue: 'bg-blue-100 text-blue-900',
  indigo: 'bg-indigo-100 text-indigo-900',
  violet: 'bg-violet-100 text-violet-900',
  purple: 'bg-purple-100 text-purple-900',
  fuchsia: 'bg-fuchsia-100 text-fuchsia-900',
  pink: 'bg-pink-100 text-pink-900',
  rose: 'bg-rose-100 text-rose-900',
};

export const OUTCOME_COLOR_NAMES = Object.keys(OUTCOME_COLOR_CLASSES);

export const DEFAULT_OUTCOME_BADGE = 'bg-slate-100 text-slate-900';

/** Badge classes for an outcome color name, with a neutral fallback. */
export function getOutcomeColorClass(color?: string | null): string {
  return (color && OUTCOME_COLOR_CLASSES[color]) || DEFAULT_OUTCOME_BADGE;
}

/**
 * @deprecated Semantics now live on outcome_definitions.counts_as_answered.
 * Kept only as a fallback until all consumers read the dynamic definitions.
 */
export const ANSWERED_OUTCOMES: CallOutcome[] = [
  'PICKED_UP',
  'AVAILABILITY_CAPTURED',
  'LEAD_CAPTURED',
  'ALREADY_PURCHASED',
  'CALLBACK_REQUESTED',
  'FINANCE_INQUIRY',
  'REFUND_REQUEST',
  'ADMINISTRATIVE',
  'TRANSFERRED_TO_HUMAN',
  'SHIFT_CONFIRMED',
  'SHIFT_DECLINED',
  'ATTENDING_USD_EVENT',
  'NOT_ATTENDING_USD_EVENT',
  'AVAILABLE_COOK',
  'NOT_AVAILABLE_COOK',
];

/**
 * @deprecated Semantics now live on outcome_definitions.expects_data_capture.
 * Kept only as a fallback until all consumers read the dynamic definitions.
 */
export const DATA_CAPTURED_OUTCOMES: CallOutcome[] = [
  'AVAILABILITY_CAPTURED',
  'LEAD_CAPTURED',
  'ALREADY_PURCHASED',
  'SHIFT_CONFIRMED',
  'ATTENDING_USD_EVENT',
  'AVAILABLE_COOK',
];

export const CALL_STATUS_BADGES: Record<CallStatus, string> = {
  QUEUED: 'bg-slate-200 text-slate-900',
  INITIATED: 'bg-blue-100 text-blue-900',
  RINGING: 'bg-cyan-100 text-cyan-900',
  IN_PROGRESS: 'bg-amber-100 text-amber-900',
  CANCELED: 'bg-gray-100 text-gray-900',
  COMPLETED: 'bg-emerald-100 text-emerald-900',
  BUSY: 'bg-orange-100 text-orange-900',
  NO_ANSWER: 'bg-yellow-100 text-yellow-900',
  FAILED: 'bg-rose-100 text-rose-900',
};

export const CALL_DIRECTION_BADGES: Record<CallDirection, string> = {
  OUTBOUND: 'bg-primary/10 text-primary',
  INBOUND: 'bg-blue-100 text-blue-900',
};

export const CALL_TRANSPORT_BADGES: Record<CallTransport, string> = {
  PSTN: 'bg-slate-100 text-slate-900',
  WEBRTC: 'bg-cyan-100 text-cyan-900',
  WHATSAPP: 'bg-green-100 text-green-900',
  WEB_WIDGET: 'bg-purple-100 text-purple-900',
  EMAIL: 'bg-amber-100 text-amber-900',
};

// Common outcomes shared by all clients
/** @deprecated The outcome list is dynamic — fetch GET /outcomes/effective instead. */
const COMMON_OUTCOMES: CallOutcome[] = [
  'PICKED_UP',
  'NO_ANSWER',
  'BUSY',
  'VOICEMAIL',
  'CALLBACK_REQUESTED',
  'TRANSFERRED_TO_HUMAN',
  'CANCELED',
  'FAILED',
];

// Client-specific outcomes (on top of common)
const CLIENT_SPECIFIC_OUTCOMES: Record<string, CallOutcome[]> = {
  NEW_CROSS: [
    'AVAILABILITY_CAPTURED',
    'FUTURE_AVAILABILITY',
    'ATTENDING_USD_EVENT',
    'NOT_ATTENDING_USD_EVENT',
    'AVAILABLE_COOK',
    'NOT_AVAILABLE_COOK',
  ],
  V12: [
    'LEAD_CAPTURED',
    'ALREADY_PURCHASED',
    'FINANCE_INQUIRY',
    'REFUND_REQUEST',
    'ADMINISTRATIVE',
    'TRANSFERRED_TO_HUMAN',
  ],
  ROYALTY_STAFFING: ['SHIFT_CONFIRMED', 'SHIFT_DECLINED'],
};

/**
 * Get the list of outcomes relevant to a client.
 * Returns common outcomes + client-specific ones.
 * Falls back to all outcomes if client is unknown.
 * @deprecated The outcome list is dynamic — fetch GET /outcomes/effective instead.
 */
export function getClientOutcomes(clientName: string): CallOutcome[] {
  const specific = CLIENT_SPECIFIC_OUTCOMES[clientName];
  if (!specific) {
    // Unknown client — return all outcomes
    return [...COMMON_OUTCOMES, ...Object.values(CLIENT_SPECIFIC_OUTCOMES).flat()] as CallOutcome[];
  }
  return [...COMMON_OUTCOMES, ...specific];
}

/**
 * @deprecated Static fallback for the legacy seeded keys. Prefer
 * getOutcomeColorClass(definition.color) with the dynamic definitions.
 */
export const CALL_OUTCOME_BADGES: Record<CallOutcome, string> = {
  PICKED_UP: 'bg-emerald-100 text-emerald-900',
  NO_ANSWER: 'bg-yellow-100 text-yellow-900',
  BUSY: 'bg-orange-100 text-orange-900',
  VOICEMAIL: 'bg-purple-100 text-purple-900',
  AVAILABILITY_CAPTURED: 'bg-green-100 text-green-900',
  FUTURE_AVAILABILITY: 'bg-sky-100 text-sky-900',
  LEAD_CAPTURED: 'bg-blue-100 text-blue-900',
  ALREADY_PURCHASED: 'bg-indigo-100 text-indigo-900',
  CALLBACK_REQUESTED: 'bg-teal-100 text-teal-900',
  TRANSFERRED_TO_HUMAN: 'bg-violet-100 text-violet-900',
  SHIFT_CONFIRMED: 'bg-green-100 text-green-900',
  SHIFT_DECLINED: 'bg-red-100 text-red-900',
  ATTENDING_USD_EVENT: 'bg-emerald-100 text-emerald-900',
  NOT_ATTENDING_USD_EVENT: 'bg-red-100 text-red-900',
  AVAILABLE_COOK: 'bg-emerald-100 text-emerald-900',
  NOT_AVAILABLE_COOK: 'bg-red-100 text-red-900',
  CANCELED: 'bg-gray-100 text-gray-900',
  FAILED: 'bg-rose-100 text-rose-900',
  FINANCE_INQUIRY: 'bg-amber-100 text-amber-900',
  REFUND_REQUEST: 'bg-rose-100 text-rose-900',
  ADMINISTRATIVE: 'bg-slate-100 text-slate-900',
};
